/**
 * Feature Gate Components
 *
 * Components for conditionally rendering UI based on tenant feature flags.
 * Provides declarative feature gating with optional upgrade prompts.
 *
 * Part of: AI Platform v5.22.0 - Phase 3.5.1 Feature Flag Enforcement
 */

import React from 'react';
import { useFeatureFlag, useMultipleFeatures, AVAILABLE_FEATURES } from '../../hooks/useFeatureFlag';

// =============================================================================
// Types
// =============================================================================

export interface FeatureGateProps {
  /**
   * Feature flag name to check (e.g., 'aiChat', 'dataExport')
   */
  feature: string;

  /**
   * Content to render when feature is enabled
   */
  children: React.ReactNode;

  /**
   * Optional content to render when feature is disabled.
   * If not provided, renders nothing when disabled.
   */
  fallback?: React.ReactNode;
}

export interface MultiFeatureGateProps {
  /**
   * Array of feature names to check
   */
  features: string[];

  /**
   * 'all' requires all features, 'any' requires at least one
   * @default 'all'
   */
  mode?: 'all' | 'any';

  /**
   * Content to render when feature check passes
   */
  children: React.ReactNode;

  /**
   * Optional content to render when feature check fails
   */
  fallback?: React.ReactNode;
}

export interface UpgradePromptProps {
  /**
   * Feature name to display in the prompt
   */
  feature: string;

  /**
   * Optional custom title
   */
  title?: string;

  /**
   * Optional custom description
   */
  description?: string;

  /**
   * Whether to show compact version
   * @default false
   */
  compact?: boolean;

  /**
   * Optional callback when contact admin is clicked
   */
  onContactAdmin?: () => void;
}

// =============================================================================
// Styles
// =============================================================================

const upgradePromptStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    backgroundColor: 'rgba(124, 58, 237, 0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(124, 58, 237, 0.2)',
    textAlign: 'center',
  },
  containerCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'rgba(124, 58, 237, 0.05)',
    borderRadius: '8px',
    border: '1px solid rgba(124, 58, 237, 0.2)',
  },
  icon: {
    width: '48px',
    height: '48px',
    marginBottom: '16px',
    color: '#7c3aed',
  },
  iconCompact: {
    width: '24px',
    height: '24px',
    color: '#7c3aed',
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '8px',
  },
  titleCompact: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  description: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '16px',
    maxWidth: '400px',
  },
  descriptionCompact: {
    fontSize: '13px',
    color: '#6b7280',
    flex: 1,
  },
  featureBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
    color: '#7c3aed',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    marginBottom: '16px',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonCompact: {
    padding: '6px 12px',
    backgroundColor: '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};

// =============================================================================
// Components
// =============================================================================

/**
 * FeatureGate - Conditionally render children based on a single feature flag.
 *
 * @example
 * ```tsx
 * <FeatureGate feature="aiChat">
 *   <AIChatButton />
 * </FeatureGate>
 *
 * <FeatureGate
 *   feature="advancedAnalytics"
 *   fallback={<UpgradePrompt feature="advancedAnalytics" />}
 * >
 *   <AnalyticsDashboard />
 * </FeatureGate>
 * ```
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback = null,
}) => {
  const isEnabled = useFeatureFlag(feature);

  if (!isEnabled) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * MultiFeatureGate - Conditionally render based on multiple features.
 *
 * @example
 * ```tsx
 * // Requires all features
 * <MultiFeatureGate features={['advancedAnalytics', 'dataExport']}>
 *   <ExportAnalyticsButton />
 * </MultiFeatureGate>
 *
 * // Requires any feature
 * <MultiFeatureGate features={['aiChat', 'advancedAnalytics']} mode="any">
 *   <PowerUserTools />
 * </MultiFeatureGate>
 * ```
 */
export const MultiFeatureGate: React.FC<MultiFeatureGateProps> = ({
  features,
  mode = 'all',
  children,
  fallback = null,
}) => {
  const isEnabled = useMultipleFeatures(features, mode);

  if (!isEnabled) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * UpgradePrompt - Display a prompt encouraging users to upgrade for a feature.
 *
 * @example
 * ```tsx
 * <FeatureGate
 *   feature="advancedAnalytics"
 *   fallback={<UpgradePrompt feature="advancedAnalytics" />}
 * >
 *   <AnalyticsView />
 * </FeatureGate>
 * ```
 */
export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  feature,
  title,
  description,
  compact = false,
  onContactAdmin,
}) => {
  const featureDescription = AVAILABLE_FEATURES[feature] || feature;
  const displayTitle = title || 'Feature Not Available';
  const displayDescription =
    description ||
    `This feature requires "${featureDescription}" to be enabled for your organization.`;

  const handleContactClick = () => {
    if (onContactAdmin) {
      onContactAdmin();
    } else {
      // Default behavior: mailto admin
      window.location.href = 'mailto:admin@example.com?subject=Feature Upgrade Request';
    }
  };

  if (compact) {
    return (
      <div style={upgradePromptStyles.containerCompact}>
        <LockIcon style={upgradePromptStyles.iconCompact} />
        <span style={upgradePromptStyles.descriptionCompact}>{displayDescription}</span>
        <button
          style={upgradePromptStyles.buttonCompact as React.CSSProperties}
          onClick={handleContactClick}
          type="button"
        >
          Upgrade
        </button>
      </div>
    );
  }

  return (
    <div style={upgradePromptStyles.container}>
      <LockIcon style={upgradePromptStyles.icon} />
      <span style={upgradePromptStyles.featureBadge}>{feature}</span>
      <h3 style={upgradePromptStyles.title}>{displayTitle}</h3>
      <p style={upgradePromptStyles.description}>{displayDescription}</p>
      <button
        style={upgradePromptStyles.button}
        onClick={handleContactClick}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#6d28d9';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = '#7c3aed';
        }}
        type="button"
      >
        Contact Administrator
        <ArrowRightIcon />
      </button>
    </div>
  );
};

/**
 * FeatureDisabled - Simple message for disabled features.
 * Less prominent than UpgradePrompt.
 */
export const FeatureDisabled: React.FC<{ feature: string }> = ({ feature }) => {
  const featureDescription = AVAILABLE_FEATURES[feature] || feature;

  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: '#f3f4f6',
        borderRadius: '8px',
        color: '#6b7280',
        fontSize: '14px',
      }}
    >
      <LockIcon
        style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }}
      />
      {featureDescription} is not available in your current plan.
    </div>
  );
};

// =============================================================================
// Icons (inline SVGs to avoid external dependencies)
// =============================================================================

const LockIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg
    style={style}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const ArrowRightIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

export default FeatureGate;
